// Sessions API — per-client CRUD, sets/cardio logging, apply-template, calendar.
import { http } from "./http";
import type {
  CalendarSession,
  CardioEntry,
  CardioInput,
  CheckinInput,
  CheckinQrResponse,
  Session,
  SessionDetail,
  SessionInput,
  SetEntry,
  SetInput,
  OkResponse,
} from "./types";

export interface CalendarRange {
  start?: string;
  end?: string;
  // Index signature keeps this assignable to the http `Query` param type.
  [key: string]: string | undefined;
}

export interface ApplyTemplateResult extends SessionDetail {
  created: number;
}

export const sessionsApi = {
  listForClient: (clientId: number, signal?: AbortSignal) =>
    http.get<Session[]>(`/clients/${clientId}/sessions`, undefined, signal),
  create: (clientId: number, body: SessionInput) =>
    http.post<Session>(`/clients/${clientId}/sessions`, body),
  get: (sessionId: number, signal?: AbortSignal) =>
    http.get<SessionDetail>(`/sessions/${sessionId}`, undefined, signal),
  update: (sessionId: number, body: SessionInput) =>
    http.patch<Session>(`/sessions/${sessionId}`, body),
  remove: (sessionId: number) =>
    http.del<OkResponse>(`/sessions/${sessionId}`),

  /**
   * Trainer marks an owned session ATTENDED (records check-in) → attendance
   * 'attended' + checked_in_at + status flips to 'completed'. `checked_in_at`
   * is optional (defaults to now); the SPA may send an explicit local time.
   */
  checkin: (sessionId: number, body: CheckinInput = {}) =>
    http.post<Session>(`/sessions/${sessionId}/checkin`, body),
  /** Trainer marks an owned session a NO-SHOW (kept on the row, excluded from income). */
  noShow: (sessionId: number) =>
    http.post<Session>(`/sessions/${sessionId}/no-show`),
  /**
   * Mint/rotate a per-session QR check-in token + absolute public URL. Rotates
   * on every call (an old QR stops working). The returned `checkin_url` is what
   * the client scans to confirm arrival via the public token-gated page.
   */
  checkinQr: (sessionId: number) =>
    http.post<CheckinQrResponse>(`/sessions/${sessionId}/checkin-qr`),

  addSet: (sessionId: number, body: SetInput) =>
    http.post<SetEntry>(`/sessions/${sessionId}/sets`, body),
  addCardio: (sessionId: number, body: CardioInput) =>
    http.post<CardioEntry>(`/sessions/${sessionId}/cardio`, body),
  applyTemplate: (sessionId: number, templateId: number) =>
    http.post<ApplyTemplateResult>(`/sessions/${sessionId}/apply-template`, {
      template_id: templateId,
    }),

  /** Trainer-wide calendar feed (drives the dashboard calendar). */
  calendar: (range: CalendarRange = {}, signal?: AbortSignal) =>
    http.get<CalendarSession[]>("/calendar", range, signal),
};
